import { gsap } from 'gsap'

export default function initMagnetic(options = {}) {
    const {
        selector = '.button',
        childSelector = '.round-element',
        maxDist = 36,
        yFactor = 0.5,
        scaleX = 0.12,
        scaleY = 1.2,
        disableOnTouch = true,
        observe = true,
    } = options

    if (typeof window === 'undefined') return { destroy: () => {} }

    const isTouch = (typeof window !== 'undefined') && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    // Si disableOnTouch est true, on désactive l'animation, mais on crée quand même
    // la .round-element afin qu'elle soit visible / stylable sur mobile.
    const animateEnabled = !(disableOnTouch && isTouch)

    const attached = new Set()
    const disposers = new Map()

    function attach(el) {
        if (!el || attached.has(el)) return

        let target = el.querySelector(childSelector)
        if (!target) {
            target = document.createElement('div')
            target.className = childSelector.replace(/^\./, '')
            el.insertBefore(target, el.firstChild)
        }

        // Si l'animation est désactivée (ex: mobile), on ne branche pas les listeners
        // mais on conserve l'élément .round-element dans le DOM pour affichage.
        if (!animateEnabled) {
            attached.add(el)
            disposers.set(el, () => {
                // simple cleanup si on veut retirer la target plus tard (optionnel)
                disposers.delete(el)
                attached.delete(el)
            })
            return
        }

        // prepare gsap quickSetters
        const qx = gsap.quickSetter(target, 'x', 'px')
        const qy = gsap.quickSetter(target, 'y', 'px')
        const qScale = gsap.quickSetter(target, 'scale')

        const bounds = { w: 0, h: 0, left: 0, top: 0 }
        const updateBounds = () => {
            const r = el.getBoundingClientRect()
            bounds.w = r.width
            bounds.h = r.height
            bounds.left = r.left + window.scrollX
            bounds.top = r.top + window.scrollY
        }

        const computeMaxDist = () => Math.min(maxDist, Math.max(8, Math.round(bounds.w * 0.12)))

        const onMove = (ev) => {
            updateBounds()
            const clientX = ev.clientX || (ev.touches && ev.touches[0] && ev.touches[0].clientX)
            const clientY = ev.clientY || (ev.touches && ev.touches[0] && ev.touches[0].clientY)
            const relX = clientX - (bounds.left + bounds.w / 2)
            const relY = clientY - (bounds.top + bounds.h / 2)

            const nx = Math.max(-1, Math.min(1, relX / (bounds.w / 2)))
            const ny = Math.max(-1, Math.min(1, relY / (bounds.h / 2)))

            const md = computeMaxDist()
            const x = Math.round(nx * md)
            const y = Math.round(ny * (md * yFactor))

            const scale = 1.0 + Math.abs(nx) * scaleX + Math.abs(ny) * scaleY

            qx(x)
            qy(y)
            qScale(scale)
        }

        const onLeave = () => {
            gsap.to(target, { duration: 0.6, x: 0, y: 0, scale: 1, ease: 'power3.out' })
        }

        el.addEventListener('mousemove', onMove)
        el.addEventListener('mouseleave', onLeave)

        attached.add(el)
        disposers.set(el, () => {
            el.removeEventListener('mousemove', onMove)
            el.removeEventListener('mouseleave', onLeave)
            // kill tweens on target
            gsap.killTweensOf(target)
            disposers.delete(el)
            attached.delete(el)
        })
    }

    function detach(el) {
        const fn = disposers.get(el)
        if (fn) fn()
    }

    function attachAll(root = document) {
        const nodes = root.querySelectorAll(selector)
        nodes.forEach(attach)
    }

    // initial attach
    attachAll(document)

    // Observe DOM additions to attach dynamically created buttons
    let observer = null
    if (observe && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === 'childList') {
                    m.addedNodes.forEach((node) => {
                        try {
                            if (!(node instanceof HTMLElement)) return
                            if (node.matches && node.matches(selector)) attach(node)
                            // also check descendants
                            attachAll(node)
                        } catch (e) {
                            // ignore
                        }
                    })
                    m.removedNodes.forEach((node) => {
                        try {
                            if (!(node instanceof HTMLElement)) return
                            if (node.matches && node.matches(selector)) detach(node)
                            const found = node.querySelectorAll ? node.querySelectorAll(selector) : []
                            found.forEach(detach)
                        } catch (e) {}
                    })
                }
            }
        })
        observer.observe(document.body, { childList: true, subtree: true })
    }

    function destroy() {
        if (observer) observer.disconnect()
        disposers.forEach((fn) => fn())
        disposers.clear()
        attached.clear()
    }

    return { destroy }
}
